import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fileUrl, api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ROLE_LABELS } from "../lib/methodology";
import { LogOut, LayoutDashboard, FolderKanban, BookOpen, Building2, CreditCard, ShieldCheck, Receipt, BarChart3, Tag, Trash2, Smile, TrendingUp, AlertTriangle, ChevronUp, KeyRound, Mail, Menu, X, ClipboardCheck, HelpCircle } from "lucide-react";
import ChangePasswordDialog from "./ChangePasswordDialog";
import ForcePasswordChange from "./ForcePasswordChange";
import useIdleLogout from "../hooks/useIdleLogout";

export default function AppLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [changeOpen, setChangeOpen] = useState(false);
    const [myBadge, setMyBadge] = useState(0);
    const menuRef = useRef(null);
    const companyLogo = user?.company?.logo_path ? fileUrl(user.company.logo_path) : null;
    const isSuper = user?.role === "super_admin";
    const isManager = user?.role === "company_manager";
    const isHappinessManager = user?.role === "happiness_manager";
    const canAdminCompany = isManager || isSuper;
    const canManageUsers = isManager || isHappinessManager || isSuper;
    const sub = user?.subscription;
    const subActive = isSuper || sub?.active;
    const showSubscriptionUI = isManager;
    const showWarning = showSubscriptionUI && sub?.warning;
    const isExpiredOrNone = showSubscriptionUI && sub && !sub.active;

    const idleMinutes = user?.company?.idle_timeout_minutes || 30;
    useIdleLogout({
        minutes: idleMinutes,
        enabled: !!user,
        warnBeforeSec: 60,
        onWarning: () => toast.warning("A sessão vai terminar em 1 minuto por inatividade.", { duration: 8000 }),
        onTimeout: async () => {
            toast.info("Sessão terminada por inatividade.");
            await logout();
            nav("/login");
        },
    });

    useEffect(() => {
        const onClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    // Lock body scroll when mobile drawer open
    useEffect(() => {
        document.body.style.overflow = mobileNavOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [mobileNavOpen]);

    // Personal badge: overdue tasks + sessions this week assigned to me.
    // Refreshed every 5 minutes. Failures are silent (sidebar must not break).
    useEffect(() => {
        if (!user) return undefined;
        let cancelled = false;
        const load = () => {
            api.get("/dashboard/summary?scope=mine")
                .then((r) => {
                    if (cancelled) return;
                    const count = (r.data?.overdue_tasks?.length || 0)
                                + (r.data?.upcoming_tasks?.length || 0)
                                + (r.data?.upcoming_sessions?.length || 0);
                    setMyBadge(count);
                })
                .catch(() => { /* swallow */ });
        };
        load();
        const t = setInterval(load, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user]);

    const sendRecoveryAndLogout = async () => {
        if (!user?.email) return;
        if (!window.confirm("Vamos enviar instruções para o seu email e terminar a sessão. Continuar?")) return;
        try {
            await api.post("/auth/forgot-password", { email: user.email });
            toast.success("Se existir, foi enviado um link para o seu email.");
        } catch (err) {
            toast.error(formatApiError(err));
        }
        await logout();
        nav("/login");
    };

    const navItem = "flex items-center gap-3 px-4 py-3 text-sm text-[#0d1628] hover:bg-[#f4f4f5] transition-colors";
    const active = "bg-[#0d1628] text-white hover:bg-[#0d1628]";

    const closeMobileNav = () => setMobileNavOpen(false);

    // Centralised navigation manifest. Each entry contains a `show` predicate
    // so role-based visibility logic stays declarative + easy to audit.
    const navItems = [
        { to: "/app/dashboard", icon: LayoutDashboard, label: "Painel", testId: "nav-dashboard", show: true },
        { to: "/app/my-tasks", icon: ClipboardCheck, label: "Os meus afazeres", testId: "nav-my-tasks", show: !isSuper, badge: myBadge },
        { to: "/app/projects", icon: FolderKanban, label: "Projetos", testId: "nav-projects", show: true },
        { to: "/app/metodologia", icon: BookOpen, label: "Metodologia", testId: "nav-methodology",
          show: isSuper },
        { to: "/app/company", icon: Building2, label: canAdminCompany ? "Empresa" : "Equipa", testId: "nav-company-admin",
          show: canManageUsers && !isSuper },
        { to: "/app/questions", icon: HelpCircle, label: "Perguntas do inquérito", testId: "nav-questions",
          show: canManageUsers && !isSuper },
        { to: "/app/subscription", icon: CreditCard, label: "Subscrição", testId: "nav-subscription",
          show: showSubscriptionUI },
        { to: "/app/super", icon: ShieldCheck, label: "Super Admin", testId: "nav-super-admin", show: isSuper },
        { to: "/app/super/dashboard", icon: BarChart3, label: "Receita", testId: "nav-admin-dashboard", show: isSuper },
        { to: "/app/super/invoices", icon: Receipt, label: "Faturas", testId: "nav-admin-invoices", show: isSuper },
        { to: "/app/super/plans", icon: Tag, label: "Planos", testId: "nav-admin-plans", show: isSuper },
        { to: "/app/super/happiness-admin", icon: Smile, label: "Inquéritos", testId: "nav-admin-happiness", show: isSuper },
        { to: "/app/super/happiness-stats", icon: TrendingUp, label: "Felicidade Global", testId: "nav-admin-happiness-stats", show: isSuper },
        { to: "/app/super/trash", icon: Trash2, label: "Lixo", testId: "nav-admin-trash", show: isSuper },
    ];

    const renderNavLinks = (onNavigate) => navItems.filter((item) => item.show).map(({ to, icon: Icon, label, testId, badge }) => (
        <NavLink key={to} onClick={onNavigate} to={to} data-testid={testId}
            className={({ isActive }) => `${navItem} ${isActive ? active : ""}`}>
            <Icon size={16} />
            <span className="flex-1">{label}</span>
            {badge > 0 && (
                <span
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-medium bg-[#b89b6f] text-white rounded-full"
                    data-testid={`${testId}-badge`}
                >
                    {badge > 99 ? "99+" : badge}
                </span>
            )}
        </NavLink>
    ));

    const renderSessionMenu = (inDrawer = false) => (
        <div className={`mt-auto border-t border-[#e4e4e7] p-4 relative`} ref={inDrawer ? null : menuRef}>
            {(menuOpen || inDrawer) && (
                <div className={inDrawer ? "" : "absolute bottom-full left-4 right-4 mb-2 bg-white border border-[#e4e4e7] shadow-lg"} data-testid={inDrawer ? "user-menu-mobile" : "user-menu"}>
                    <button
                        onClick={() => { setChangeOpen(true); setMenuOpen(false); closeMobileNav(); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-[#0d1628] hover:bg-[#f4f4f5] text-left ${inDrawer ? "border-t border-[#e4e4e7]" : ""}`}
                        data-testid={inDrawer ? "menu-change-password-mobile" : "menu-change-password"}
                    >
                        <KeyRound size={14} /> Alterar palavra-passe
                    </button>
                    <button
                        onClick={() => { sendRecoveryAndLogout(); setMenuOpen(false); closeMobileNav(); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#0d1628] hover:bg-[#f4f4f5] text-left border-t border-[#e4e4e7]"
                        data-testid={inDrawer ? "menu-recover-password-mobile" : "menu-recover-password"}
                    >
                        <Mail size={14} /> Recuperar palavra-passe
                    </button>
                    <button
                        onClick={async () => { closeMobileNav(); await logout(); nav("/login"); }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-[#f4f4f5] text-left border-t border-[#e4e4e7]"
                        data-testid={inDrawer ? "menu-logout-mobile" : "menu-logout"}
                    >
                        <LogOut size={14} /> Terminar sessão
                    </button>
                </div>
            )}
            {!inDrawer && (
                <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="w-full flex items-center justify-between gap-3 hover:bg-[#f4f4f5] p-2 -m-2 transition-colors"
                    data-testid="user-menu-trigger"
                >
                    <div className="text-left">
                        <div className="text-xs text-[#52525b]">Sessão</div>
                        <div className="text-sm text-[#0d1628] font-medium mt-1" data-testid="current-user-name">{user?.name}</div>
                        <div className="label-caps mt-1">{ROLE_LABELS[user?.role] || user?.role}</div>
                    </div>
                    <ChevronUp size={16} className={`text-[#52525b] transition-transform ${menuOpen ? "" : "rotate-180"}`} />
                </button>
            )}
            {inDrawer && (
                <div className="px-4 py-3 bg-[#f9f9fa]">
                    <div className="text-xs text-[#52525b]">Sessão</div>
                    <div className="text-sm text-[#0d1628] font-medium mt-1">{user?.name}</div>
                    <div className="label-caps mt-1">{ROLE_LABELS[user?.role] || user?.role}</div>
                </div>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-white flex" data-testid="app-layout">
            {/* Force temporary password change on first login */}
            {user?.must_change_password && <ForcePasswordChange />}

            {/* Desktop sidebar */}
            <aside className="hidden md:flex w-64 shrink-0 border-r border-[#e4e4e7] flex-col">
                <div className="min-h-[9rem] border-b border-[#e4e4e7] flex items-center justify-center p-6 bg-[#f9f9fa]">
                    {companyLogo ? (
                        <img src={companyLogo} alt={user.company?.name} className="max-h-24 max-w-full object-contain" data-testid="company-logo" />
                    ) : (
                        <div className="text-center">
                            <div className="font-display text-3xl text-[#0d1628]" data-testid="company-name">
                                {user?.company?.name || "IHTP"}
                            </div>
                            {user?.company?.name && <div className="label-caps mt-2">IHTP Platform</div>}
                        </div>
                    )}
                </div>
                <nav className="flex flex-col py-6">
                    {renderNavLinks()}
                </nav>
                {renderSessionMenu()}
            </aside>

            {/* Mobile drawer */}
            {mobileNavOpen && (
                <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
                    <div className="absolute inset-0 bg-[#0d1628]/60" onClick={closeMobileNav} />
                    <aside className="relative bg-white w-72 max-w-[85vw] h-full flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="min-h-[7rem] border-b border-[#e4e4e7] flex items-center justify-between p-4 bg-[#f9f9fa]">
                            <div className="flex-1 flex items-center justify-center">
                                {companyLogo ? (
                                    <img src={companyLogo} alt={user.company?.name} className="max-h-16 max-w-full object-contain" />
                                ) : (
                                    <div className="text-center">
                                        <div className="font-display text-2xl text-[#0d1628]">{user?.company?.name || "IHTP"}</div>
                                    </div>
                                )}
                            </div>
                            <button onClick={closeMobileNav} className="p-2 text-[#52525b] hover:text-[#0d1628]" data-testid="close-mobile-nav">
                                <X size={20} />
                            </button>
                        </div>
                        <nav className="flex flex-col py-4 overflow-y-auto flex-1">
                            {renderNavLinks(closeMobileNav)}
                        </nav>
                        {renderSessionMenu(true)}
                    </aside>
                </div>
            )}

            <ChangePasswordDialog open={changeOpen} onClose={() => setChangeOpen(false)} />

            <div className="flex-1 flex flex-col min-w-0">
                <header className="h-16 md:h-20 border-b border-[#e4e4e7] flex items-center justify-between px-4 md:px-10 gap-3">
                    <div className="md:hidden flex items-center gap-3 flex-1 min-w-0">
                        <button onClick={() => setMobileNavOpen(true)} className="p-2 -ml-2 text-[#0d1628]" data-testid="open-mobile-nav">
                            <Menu size={22} />
                        </button>
                        {companyLogo ? (
                            <img src={companyLogo} alt="logo" className="h-8 max-w-[120px] object-contain" />
                        ) : (
                            <span className="font-display text-lg text-[#0d1628] truncate">{user?.company?.name || "IHTP"}</span>
                        )}
                    </div>
                    <div className="hidden md:flex items-center gap-6">
                        <span className="label-caps">Plataforma IHTP</span>
                        {showSubscriptionUI && sub?.active && sub.ends_at && (
                            <span className="text-xs text-[#52525b]" data-testid="header-sub-info">
                                Subscrição {sub.plan_name} · termina {sub.ends_at.slice(0, 10)}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3 md:gap-4">
                        <Link to="/" className="text-xs text-[#52525b] link-underline hidden sm:inline" data-testid="back-to-site">Voltar ao site</Link>
                        <button
                            onClick={async () => { await logout(); nav("/login"); }}
                            className="text-xs text-[#0d1628] link-underline flex items-center gap-1.5"
                            data-testid="header-logout"
                        >
                            <LogOut size={12} /> Sair
                        </button>
                    </div>
                </header>

                {showSubscriptionUI && isExpiredOrNone && (
                    <div className="bg-[#0d1628] text-white px-4 md:px-10 py-3 md:py-4 flex items-center justify-between gap-3 flex-wrap" data-testid="sub-expired-banner">
                        <div className="flex items-start gap-3">
                            <AlertTriangle size={18} className="text-[#b89b6f] shrink-0 mt-0.5" />
                            <div className="text-sm">
                                <strong className="font-medium">
                                    {sub?.status === "expired" ? "A sua subscrição expirou." :
                                     sub?.status === "pending" ? "Subscrição aguarda ativação." :
                                     "A sua empresa ainda não tem subscrição ativa."}
                                </strong>
                                <span className="text-white/70 ml-2 hidden sm:inline">Algumas ações estão bloqueadas até adquirir um plano.</span>
                            </div>
                        </div>
                        <Link to="/app/subscription" className="btn-primary text-xs md:text-sm" style={{ background: "#fff", color: "#0d1628", borderColor: "#fff", padding: "0.5rem 1rem" }} data-testid="banner-cta-subscribe">
                            Adquirir
                        </Link>
                    </div>
                )}
                {showSubscriptionUI && showWarning && (
                    <div className="bg-[#b89b6f]/15 border-b border-[#b89b6f] px-4 md:px-10 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="sub-warning-banner">
                        <div className="flex items-start gap-3 text-sm text-[#0d1628]">
                            <AlertTriangle size={16} className="text-[#b89b6f] shrink-0 mt-0.5" />
                            <span>Termina em <strong>{sub.days_remaining}</strong> dia(s) ({sub.ends_at?.slice(0, 10)})</span>
                        </div>
                        <Link to="/app/subscription" className="btn-ghost text-[#0d1628] text-xs md:text-sm" data-testid="banner-cta-renew">Renovar</Link>
                    </div>
                )}

                <main className="flex-1 p-4 md:p-10 overflow-x-hidden">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
